import { FC, HTMLAttributes, useState, useEffect, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import styled, { css } from "styled-components";
import { Bounties } from "components/Bounties";
import { Player } from "components/Player";
import { Hud } from "components/Hud";
import { TicketMachine } from "components/TicketMachine";
import { useStore } from "utils/store";
import {
  useCards,
  useCurrency,
  usePlayer,
  usePlayers,
  useTournamentDetails,
} from "hooks";
import { CommunityCards } from "components/CommunityCards";
import { animated, easings, useSpring } from "@react-spring/web";
import { ScratchCard } from "components/ScratchCard";
import { GreenBox, Overlay as OverlayStyle } from "utils/constants";
import { GameState, MediaQuery, MinHeight } from "types";
import { IncompleteGameModal } from "components/IncompleteGameModal";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback, useTimeout, useWindowSize } from "usehooks-ts";
import { useLayout } from "components/Layout";
import { useWindowFocus } from "hooks/useWindowFocus";
import { BountiesWon } from "components/BountiesWon";
import { BountiesTracker } from "components/BountiesTracker";
import { useOrientation } from "hooks/orientation";
import { useSkinnedAssets } from "hooks/skin";

const Logo = styled.div<{ $logo: string }>`
  width: 16vw;
  height: 28vh;
  background: url(${(props) => props.$logo}) right bottom / contain no-repeat;
  position: absolute;

  @media (orientation: landscape) {
    right: 0.4vw;
    bottom: 0;
  }

  @media (orientation: portrait) {
    width: 39vw;
    height: 15vh;
    background: url(${(props) => props.$logo}) center / contain no-repeat;
    left: calc((100% - 39vw) / 2);
  }
`;

const Container = styled.div<{ $bgDesktop: string; $height: number }>`
  padding-top: clamp(0.625rem, -0.932rem + 5.301vh, 2rem);
  padding-bottom: clamp(0.394rem, -1.142rem + 5.229vh, 1.75rem);
  padding-left: 6.3vw;
  padding-right: 6.3vw;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: radial-gradient(
      circle min(0vw, 37.5vw) at left bottom,
      rgba(126, 72, 30, 0.6),
      rgba(100, 83, 83, 0) 72%
    ),
    url(${(props) => props.$bgDesktop});
  //background-position: max(${(props) =>
    600 + (110 * props.$height) / 885}px, 100%) center;
  background-position: right center;
  overflow: hidden;
  background-size: auto 100%;
  background-repeat: no-repeat;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: clamp(16.5rem, 10.656rem + 18.701vh, 21rem) 1fr;
  grid-gap: 0 5vw;

  @media (orientation: landscape) and (${MediaQuery.ACR}) {
    padding-right: 16vw;
    background: url(${(props) => props.$bgDesktop}) right center / auto 100%
      no-repeat;
  }

  @media (orientation: portrait) {
    padding: 2.3vh 1.6vw 0;
    background: none;
    grid-template-rows: 10.4vh 1fr 18vh;
    grid-template-columns: 41.1vw 39.4vw 1fr;
    grid-gap: 2.6vh 0;
  }
`;

const LeftPanel = styled.div`
  height: 100%;
  grid-column: 1 / 2;
  z-index: 2;
`;

const RightPanel = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  grid-template-rows: 26vh 1fr;
  grid-template-columns: 12vw 1fr;
  grid-gap: 1.5vh 0;
  grid-column: 2 / 3;

  @media (orientation: portrait) {
    margin-top: 1vh;
    margin-left: 0;
    grid-row: 2 / 3;
    grid-column: 1 / 4;
    grid-template-rows: 18.9vh 1fr;
    grid-template-columns: 27.1vw 1fr;
    grid-gap: 0 1.6vw;
  }
`;

const Players = styled.div<{ $total: number }>`
  width: 100%;
  height: 100%;
  position: relative;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-row-start: 2;
  grid-column: 1 / 3;

  & > div:first-child {
    grid-column: 1 / 2;
  }

  & > div:nth-child(2) {
    grid-column: 3 / 4;
  }

  & > div:nth-child(3) {
    grid-column: 4 / 5;
  }

  ${(props) => {
    if (props.$total < 6)
      return css`
        & > div:nth-child(5) {
          grid-column: 5 / 6;
        }
      `;
    else
      return css`
        & > div:nth-child(4) {
          grid-column: 5 / 6;
        }

        & > div:nth-child(6) {
          grid-column: 6 / 7;
        }
      `;
  }}

  @media (orientation: portrait) {
    grid-template-columns: ${(props) => 20 + (6 - props.$total) * 2.5}% repeat(
        ${(props) => props.$total - 1},
        minmax(0, 1fr)
      );
  }
`;

const H1 = styled(animated.h1)`
  margin: 0;
  text-shadow: 0 2px 1px #000;
  font-size: 1.125rem;
  font-weight: normal;
  line-height: 1;
  letter-spacing: 0.8px;
  text-align: center;
  color: #fff;
  grid-column: 1 / 5;

  @media (orientation: portrait) {
    font-size: 2.5rem;
  }
`;

const Text = styled(GreenBox)`
  grid-column: 1 / 5;

  & span {
    color: #f7e70c;
  }
`;

const Instruction = styled(animated.div)<{ $width: number; $height: number }>`
  position: absolute;
  width: 254px;
  left: calc((100% - 254px) / 2);
  top: 14.5vh;
  display: grid;
  grid-template-rows: 1fr 48px 45px;
  grid-template-columns: 1fr 90px 90px 1fr;
  grid-gap: 17px 21px;
  pointer-events: none;
  transform-origin: top center;
  transform: scale(
    ${(props) =>
      Math.max(254, Math.min(478, props.$height * 0.54 + 0.32)) / 254}
  );

  & > ${H1} {
    grid-row: 1 / 2;
  }

  & > ${Text} {
    grid-row: 3 / 4;
  }

  @media (orientation: portrait) {
    width: 500px;
    left: calc(50% - 250px);
    top: 2vh;
    grid-template-rows: 1fr 105px 83px;
    grid-template-columns: 1fr 199px 199px 1fr;
    grid-gap: 36px 34px;
    transform: scale(
      ${(props) => Math.min(props.$width / 640, props.$height / 1150)}
    );
  }
`;

const Lowest = styled.div`
  width: 100%;
  height: 100%;
  font-size: 1rem;
  font-weight: 500;
  line-height: 4.5;
  letter-spacing: normal;
  text-align: center;
  color: #fff;
  border-image-slice: 88% 10% 12% fill;
  border-image-width: auto;
  border-image-source: url(images/lowest.webp);
  grid-row: 2 / 3;
  grid-column: 2 / 3;

  @media (orientation: portrait) {
    font-size: 2.3rem;
    line-height: 4.3;
    border-image-slice: 178 60 26 fill;
  }
`;

const Highest = styled(Lowest)`
  border-image-source: url(images/highest.webp);
  grid-column: 3 / 4;
`;

const Eliminated = styled.div`
  position: absolute;
  width: 22rem;
  left: calc(55vw + 11rem);
  top: calc((100% - 15vh) / 2);
  font-family: Roboto;
  font-size: min(11vw, 44px);
  font-weight: 800;
  line-height: 1;
  text-align: center;
  filter: drop-shadow(0 1px 2px #000378) drop-shadow(0 3px 0 #2038ff)
    drop-shadow(0 -3px 0 #2038ff) drop-shadow(3px 0 0 #2038ff)
    drop-shadow(-3px 0 0 #2038ff);
  background-image: repeating-linear-gradient(
    to bottom,
    #26f5ff 6%,
    #277fff 31%,
    #28a3ff 56%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (orientation: portrait) {
    left: calc((100% - 22rem) / 2);
    top: 50svh;
    transform: translateY(-50%);
  }
`;

const Overlay = styled(OverlayStyle)`
  z-index: 21;
  background-color: transparent;
  pointer-events: none;

  @media (orientation: portrait) {
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const Footer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  grid-row-start: 3;
  grid-column: 1 / 4;
  z-index: 1;
  display: grid;
  grid-template-rows: 7.7vh 3.5vh;
  grid-template-columns: 40vw 1fr;
  grid-gap: clamp(1.5rem, -0.936rem + 5.128vh, 2.75rem)
    clamp(0.5rem, -0.786rem + 5.714vw, 1.5rem);
`;

const Image = styled(animated.img)`
  object-fit: contain;
`;

const Skip = styled.button`
  height: 3.5vh;
  font-family: Teko;
  font-size: clamp(1rem, -0.286rem + 5.714vw, 2rem);
  line-height: 0.75;
  letter-spacing: 0.2vw;
  color: #b79988;
  text-decoration: underline;
  background-color: transparent;
  border: none;
  cursor: pointer;
  grid-row: 2 / 3;
  grid-column: 1 / 2;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-inline: 0;

  & > ${Image} {
    margin-left: 2vw;
  }
`;

const Scratcher = styled(Skip)`
  grid-column: 2 / 3;
`;

const Hook = styled.div`
  --height: 84px;

  width: 161px;
  height: var(--height);
  border-radius: 6px;
  box-shadow: 0 0 9px 0 rgba(255, 255, 255, 0.79);
  border: solid 1px #fff;
  background-image: linear-gradient(
    to bottom,
    #898a9f -15%,
    #fbfbfc 49%,
    #828399 109%
  );
  position: absolute;
  padding: 0 5px;
  text-shadow: 0 0.5px 0 #fff;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0.71px;
  line-height: 0.9;
  text-align: center;
  color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;

  @media (orientation: landscape) and (${MediaQuery.ACR}) {
    //top: 16px;
    //right: calc(clamp(0rem, -7.822rem + 26.627vh, 2.813rem) - 2.5rem);
  }

  &::after {
    content: "";
    position: absolute;
    width: calc(var(--height) * 0.1);
    height: calc(var(--height) * 0.1);
    background-color: #fff;
    transform: rotate(45deg);
    top: calc((100% - (var(--height) * 0.1)) / 2);
    z-index: -1;
    right: calc(var(--height) * -0.05);
  }
`;

export const Game: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const { t } = useTranslation();
  useWindowFocus();
  const [count, setCount] = useState(0);
  const { playerIds } = usePlayers();
  const setHandId = useStore((state) => state.setHandId);
  const nextHand = useStore((state) => state.nextHand);
  const setPlayers = useStore((state) => state.setPlayers);
  const nextStreet = useStore((state) => state.nextStreet);
  const updatePlayers = useStore((state) => state.updatePlayers);
  const updateAction = useStore((state) => state.updateAction);
  const updateWinnings = useStore((state) => state.updateWinnings);
  const playerCardShown = useStore((state) => state.playerCardShown);
  const updatePrize = useStore((state) => state.updatePrize);
  const showCard = useStore((state) => state.showScratchCard);
  const toggleShowScratchCard = useStore(
    (state) => state.toggleShowScratchCard
  );
  const setShowCard = useStore((state) => state.setShowScratchCard);
  const updateBank = useStore((state) => state.updateBank);
  const updateBounty = useStore((state) => state.updateBounty);
  const skipHand = useStore((state) => state.skipHand);
  const { parseCurrency } = useCurrency();
  const updateBalance = useStore((state) => state.updateBalance);
  const successEnd = useStore((state) => state.successEnd);
  const hideHand = useStore((state) => state.hideHand);
  const firstRunDone = useRef(false);
  const playersRef = useRef<HTMLDivElement>(null);
  const [bountyPlayer, setBountyPlayer] = useState("");
  const setStartingPlayer = useStore((state) => state.setStartingPlayer);
  const skip = useStore((state) => state.skip);
  const { width, height } = useWindowSize();
  const { gameState, highestBounty, lowestBounty } = useTournamentDetails();
  const [showIncompleteGame, setShowIncompleteGame] = useState(false);
  const [winnerShown, setWinnerShown] = useState(false);
  const play = useSoundStore((state) => state.play);
  const { isReady, onPlay, onLobby } = useLayout();
  const [eliminated, setEliminated] = useState(false);
  const { portrait } = useOrientation();
  const minPlayersRef = useRef(6);
  const { time } = usePlayer();
  const { communityCards } = useCards();
  const [isBountyDone, setIsBountyDone] = useState(false);
  const { bgDesktop, logoMobile, logo } = useSkinnedAssets();
  const hookRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function updatePosition() {
      const el = hookRef.current;
      if (!el) return;

      const targetX = 700;
      const targetY = 300;
      const hookRect = el.getBoundingClientRect();
      const hookX = targetX - hookRect.width;
      const hookY = targetY - hookRect.height / 2;

      const bgScale = window.innerHeight / 1620;

      const right = hookX * bgScale;
      const top = hookY * bgScale;

      el.style.top = `${top}px`;
      el.style.right = `${right}px`;
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  const [textSpring, textApi] = useSpring(() => ({
    opacity: 0,
    display: "grid",
    config: {
      duration: 250,
      easing: easings.easeOutSine,
    },
  }));

  const [spring, api] = useSpring(() => ({
    opacity: 0,
  }));

  useEffect(() => {
    api.set({ opacity: 0 });
  }, []);

  useEffect(() => {
    if (time === 1)
      minPlayersRef.current = portrait ? Math.max(playerIds.length, 4) : 6;
  }, [playerIds.length, portrait, time]);

  useEffect(() => {
    if (!!playerIds.length && !count) {
      textApi.start({ opacity: 1, delay: 100 });
    }
  }, [playerIds.length, textApi, count]);

  useTimeout(
    () => {
      setCount(count + 1);
    },
    count > 2 || !playerIds.length ? null : !count ? 1000 : 2500
  );

  useEffect(() => {
    if (successEnd) setShowCard(true);
  }, [successEnd]);

  const setNextHand = () => {
    firstRunDone.current = true;
    nextHand();
    setHandId();
    setIsBountyDone(false);
  };

  const debSetNextHand = useDebounceCallback(setNextHand, 1000);

  const hideInstruction = () => {
    textApi.start({
      opacity: 0,
      onRest: () => {
        textApi.set({ display: "none" });
      },
    });
  };

  const handleSkip = () => {
    if (!successEnd) {
      hideInstruction();
      skipHand();
    }
  };

  const handleScratcher = () => {
    toggleShowScratchCard();
  };

  const handleCards = () => {
    updateAction();
    updatePlayers(communityCards);
  };

  const handleHeroFinish = () => {
    hideInstruction();
    if (!skip) {
      if (firstRunDone.current) playerCardShown("");
      else setNextHand();
    }
  };

  const handlePlayerFinish = (id: string) => {
    if (!skip) {
      if (playerIds.slice(-1)[0] === id) {
        handleCards();
      } else {
        playerCardShown(id);
      }
    }
  };

  const handleWatch = (isFirstRun: boolean) => {
    if (isFirstRun) {
      setStartingPlayer();
    }
    setShowCard(false);
  };

  const handleTopPlayer = (id: string) => {
    setBountyPlayer(id);
  };

  const handleJackpot = () => {
    setWinnerShown(true);
  };

  const handleEliminated = () => {
    play(Sounds.Eliminated);
    api.start({
      opacity: 1,
    });
    setEliminated(true);
  };

  useEffect(() => {
    if (isReady && gameState === GameState.UnfinishedGame) {
      setShowIncompleteGame(true);
      setShowCard(true);
    }
  }, [gameState, isReady]);

  useEffect(() => {
    if (!isReady) {
      setShowCard(false);
      setEliminated(false);
      api.set({ opacity: 0 });
    }
  }, [api, isReady]);

  const handleCardsFinish = () => {
    if (skip) {
      updateBank();
    }
    updateBalance();
  };

  return (
    <Container $bgDesktop={bgDesktop} $height={height}>
      {!portrait && (
        <Hook ref={hookRef}>
          {t(
            "Win 10,000X + on a single scratch card. Keep an eye out for Big Bounties!"
          )}
        </Hook>
      )}
      <IncompleteGameModal
        show={showIncompleteGame}
        onClose={() => setShowIncompleteGame(false)}
      />
      {isReady &&
        (!playerIds.length ? (
          gameState !== GameState.UnfinishedGame && (
            <TicketMachine onFinish={handleScratcher} />
          )
        ) : (
          <>
            {(portrait || height < MinHeight) && (
              <Logo
                $logo={portrait ? logoMobile : logo}
                style={portrait && !!playerIds.length ? { left: "-4px" } : {}}
              />
            )}
            {portrait ? (
              <BountiesWon onBankUpdate={updateBounty} />
            ) : (
              <LeftPanel>
                <Bounties
                  bountyPlayer={bountyPlayer}
                  onSkip={handleSkip}
                  onScratcher={handleScratcher}
                  onFlushUpdate={updatePrize}
                  onBankUpdate={updateBounty}
                  onJackpot={handleJackpot}
                  isEliminated={eliminated}
                  isBountyDone={isBountyDone}
                />
              </LeftPanel>
            )}
            <RightPanel>
              <Hud
                winnerShown={winnerShown}
                onHand={setPlayers}
                onMcip={nextStreet}
                onLastCard={updateWinnings}
                onWinLose={updateBank}
                onFinish={hideHand}
              />
              <Instruction $width={width} $height={height} style={textSpring}>
                <H1>{t("Assigning bounties....")}</H1>
                <Lowest>{parseCurrency(lowestBounty)}</Lowest>
                <Highest>{parseCurrency(highestBounty)}</Highest>
                <Text>
                  {count < 2 ? (
                    <Trans
                      i18nKey="KO a player and win half their bounty…"
                      components={[<span />]}
                    />
                  ) : (
                    <Trans
                      i18nKey="Randomizing your starting bounty…"
                      components={[<span />]}
                    />
                  )}
                </Text>
              </Instruction>
              <CommunityCards
                cards={communityCards}
                onNextCard={handleCards}
                onFinish={handleCardsFinish}
              />
              <Players ref={playersRef} $total={minPlayersRef.current}>
                {count > 0 &&
                  playerIds.map((playerId: string, index: number) => (
                    <Player
                      key={index}
                      id={playerId}
                      onHeroFinish={handleHeroFinish}
                      onPlayerFinish={handlePlayerFinish}
                      onBalanceUpdate={debSetNextHand}
                      onTopPlayer={() => handleTopPlayer(playerId)}
                      onEliminated={handleEliminated}
                      onBountyUpdate={() => setIsBountyDone(true)}
                    />
                  ))}
              </Players>
            </RightPanel>
            {portrait && (
              <Footer>
                <BountiesTracker
                  onFlushUpdate={updatePrize}
                  onJackpot={handleJackpot}
                  bountyPlayer={bountyPlayer}
                  onSkip={handleSkip}
                  isBountyDone={isBountyDone}
                />
                <Skip onClick={handleSkip}>
                  {t("SKIP THIS HAND")}
                  <Image
                    src="images/arrow-forward.png"
                    alt="skip"
                    width={`${(width / 640) * 36}px`}
                  />
                </Skip>
                <Scratcher onClick={handleScratcher}>
                  {t("OPEN RESULTS SCRATCHER")}
                  <Image
                    src="images/arrow-open.png"
                    alt="skip"
                    width={`${(width / 640) * 27}px`}
                  />
                </Scratcher>
              </Footer>
            )}
          </>
        ))}
      <Overlay style={spring}>
        <Eliminated>{t('YOU HAVE BEEN ELIMINATED!')}</Eliminated>
      </Overlay>
      <ScratchCard
        show={showCard}
        playAgain={successEnd}
        onWatch={handleWatch}
        onLobby={onLobby}
        onPlay={() => {
          onPlay(true);
        }}
        isEliminated={eliminated}
      />
    </Container>
  );
};
